/**
 * Job C surface + row recipes, shared by `DropdownMenu` and `ContextMenu`.
 *
 * `docs/picker-taxonomy.md` §3.3, §6.9. These four strings were byte-identical
 * in `dropdown-menu.tsx` and `context-menu.tsx`; a right-click menu is the
 * right-click twin of the ⋯ menu over the same object, so the two must not be
 * able to drift.
 *
 * **Internal.** Deliberately not re-exported from the package barrel — the
 * composition a feature reaches for is the component, not the class string.
 */

/**
 * Shared surface recipe for the menu + submenu panels.
 *
 * Overlay tier per `docs/borderless-contract.md` §2: `bg-elevated` +
 * `shadow-popover`, no edge. `shadow-popover` — not `shadow-overlay` — because
 * §6 requires two elements on the same tier to carry the same shadow, and every
 * other *anchored* overlay panel uses it (`Select`, `Popover`, `Tooltip`, the
 * editor bubble menu, the mention popover). `shadow-overlay` is reserved for
 * free-floating modal surfaces: `Dialog`, `AlertDialog`, `Sheet`, drag overlays.
 */
export const menuSurface = [
  "z-50 min-w-44 overflow-hidden rounded-lg bg-elevated p-1 shadow-popover",
  "text-text",
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
  "duration-120 ease-out-fast",
].join(" ");

/**
 * Shared recipe for every selectable row inside a menu (padding excluded).
 *
 * `gap-2`, not the `gap-1.5` a value trigger uses: a menu row's leading glyph
 * is a command icon, not part of the value.
 */
export const menuItemBase = [
  "relative flex h-control cursor-default select-none items-center gap-2",
  "rounded-sm text-sm text-text outline-none",
  "transition-[background-color,color] duration-100 ease-out-fast",
  "data-[highlighted]:bg-hover focus:bg-hover",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  "[&_svg:not([class*='size-'])]:size-3.5",
  "[&_svg:not([class*='text-'])]:text-text-tertiary",
].join(" ");

/** Plain menu row. */
export const menuItem = `${menuItemBase} px-2`;

/**
 * Menu row that reserves room for a left-hand check / radio indicator.
 *
 * Left, not right: a menu row can carry a trailing shortcut, and an indicator
 * on the right would collide with it. (A listbox option is the mirror image —
 * its check goes right, clear of a leading dot or avatar.)
 */
export const menuIndicatorItem = `${menuItemBase} pl-7 pr-2`;
